import {
    MAX_PAGE_SIZE_FOR_COMPARISON,
    STATISTICS,
} from '@client/app/shared-modules/statistics/statistics.constant';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import type { StatisticsPage } from '@client/app/modules/statistics/interfaces/statistics-vistor.interface';
import type { MonTableCollection } from '@client/ng2/models/table-collection.interface';
import type {
    SiteAnalyticsDataRequest,
    SiteAnalyticsDataResult,
    SiteAnalyticsLinkCell,
    SiteAnalyticsScaffoldConfig,
    SiteAnalyticsTableRow,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import type {
    SiteAnalyticsPageDefinition,
    SiteAnalyticsPageDefinitionDeps,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-definition.type';

const BOUNCE_RATE_PAGES_CONFIG: SiteAnalyticsScaffoldConfig = {
    pageHeaderKey: 'siteAnalytics.bounceRates.header',
    pageSubheaderKey: 'siteAnalytics.bounceRates.subheader',
    tableKey: 'site-analytics-bounce-rate-pages',
    dataSource: 'bounceRatePages',
    exportCategory: 'entry_exit_bounce_rates',
    searchPlaceholderKey: 'siteAnalytics.scaffold.search.placeholder',
    emptyMessageKey: 'siteAnalytics.scaffold.table.emptyMessage',
    emptySearchMessageKey: 'siteAnalytics.scaffold.table.emptySearchMessage',
    tableCaptionKey: 'siteAnalytics.bounceRates.table.caption',
    exportButtonLabelKey: 'siteAnalytics.scaffold.export.buttonLabel',
    dateRangeLabelKey: 'siteAnalytics.scaffold.dateRange.label',
    compareToggleLabelKey: 'siteAnalytics.scaffold.compare.toggleLabel',
    compareDateRangeLabelKey: 'siteAnalytics.scaffold.compare.dateRangeLabel',
    defaultSortBy: 'bounce_rate',
    defaultSortDir: 'desc',
    comparisonMaxPageSize: MAX_PAGE_SIZE_FOR_COMPARISON,
    chart: {
        titleKey: 'siteAnalytics.bounceRates.chart.title',
        primarySeriesLabelKey: 'siteAnalytics.scaffold.chart.series.currentBounceRate',
        comparisonSeriesLabelKey: 'siteAnalytics.scaffold.chart.series.comparisonBounceRate',
        valueDisplayFormat: 'percentage',
    },
    columns: [
        {
            id: 'page',
            headerKey: 'siteAnalytics.scaffold.table.column.url',
            displayType: 'link',
            width: 'xl',
            ariaLabelKey: 'siteAnalytics.scaffold.table.urlAriaLabel{{url}}',
        },
        {
            id: 'visits',
            headerKey: 'siteAnalytics.scaffold.table.column.visits',
            displayType: 'number',
            width: 'md',
        },
        {
            id: 'visitsComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.visitsComparison',
            displayType: 'number',
            visibility: 'comparison',
            width: 'md',
        },
        {
            id: 'bounceRate',
            headerKey: 'siteAnalytics.scaffold.table.column.bounceRate',
            displayType: 'text',
            width: 'md',
        },
        {
            id: 'bounceRateComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.bounceRateComparison',
            displayType: 'text',
            visibility: 'comparison',
            width: 'md',
        },
        {
            id: 'entries',
            headerKey: 'siteAnalytics.scaffold.table.column.entries',
            displayType: 'text',
            width: 'md',
        },
        {
            id: 'entriesComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.entriesComparison',
            displayType: 'number',
            visibility: 'comparison',
            width: 'md',
        },
        {
            id: 'avgTime',
            headerKey: 'siteAnalytics.scaffold.table.column.averageTime',
            displayType: 'duration',
            width: 'md',
        },
        {
            id: 'avgTimeComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.averageTimeComparison',
            displayType: 'duration',
            visibility: 'comparison',
            width: 'md',
        },
    ],
};

const loadBounceRatePagesData = async (
    request: SiteAnalyticsDataRequest,
    deps: SiteAnalyticsPageDefinitionDeps,
): Promise<SiteAnalyticsDataResult> => {
    const { statsRepoService, statisticsParamsService } = deps;
    const isComparisonMode = Boolean(request.comparisonDateRange);

    const currentParams = statisticsParamsService.buildListParams({
        limit: request.perPage,
        page: request.page,
        domainId: request.domainId,
        dateRange: request.dateRange,
        dateFormat: STATISTICS.DATE_FORMAT,
        sortBy: request.sortBy,
        sortDirection: request.sortDir,
        search: request.search,
    });

    if (!isComparisonMode || !request.comparisonDateRange) {
        const currentBounceRatePagesResponse = await statsRepoService.getEntryPages(
            currentParams,
            contextTokens.NO_PARSE_SEARCH,
        );

        const normalizedCurrentRows = normalizeRows(currentBounceRatePagesResponse);

        return {
            rows: normalizedCurrentRows,
            chart: {
                labels: normalizedCurrentRows.map((row) => getPageLabel(row)),
                primaryValues: normalizedCurrentRows.map((row) => toFloat(row.bounceRateValue)),
            },
            summary: null,
            total: getTotal(currentBounceRatePagesResponse, normalizedCurrentRows.length),
            perPage: getPerPage(currentBounceRatePagesResponse, request.perPage),
            page: getCurrentPage(currentBounceRatePagesResponse, request.page),
        };
    }

    const comparisonParams = statisticsParamsService.buildComparisonParams(
        currentParams,
        request.comparisonDateRange,
        STATISTICS.DATE_FORMAT,
    );

    const [currentBounceRatePagesComparisonResponse, comparisonBounceRatePagesResponse] = await Promise.all([
        statsRepoService.getEntryPages(currentParams, contextTokens.NO_PARSE_SEARCH),
        statsRepoService.getEntryPages(comparisonParams, contextTokens.NO_PARSE_SEARCH),
    ]);

    const normalizedComparisonRows = normalizeRows(
        currentBounceRatePagesComparisonResponse,
        comparisonBounceRatePagesResponse,
    );

    return {
        rows: normalizedComparisonRows,
        chart: {
            labels: normalizedComparisonRows.map((row) => getPageLabel(row)),
            primaryValues: normalizedComparisonRows.map((row) => toFloat(row.bounceRateValue)),
            comparisonValues: normalizedComparisonRows.map((row) => toFloat(row.bounceRateComparisonValue)),
        },
        summary: null,
        total: getTotal(currentBounceRatePagesComparisonResponse, normalizedComparisonRows.length),
        perPage: getPerPage(currentBounceRatePagesComparisonResponse, request.perPage),
        page: getCurrentPage(currentBounceRatePagesComparisonResponse, request.page),
    };
};

function normalizeRows (
    currentBounceRatePages: MonTableCollection<StatisticsPage>,
    comparisonBounceRatePages?: MonTableCollection<StatisticsPage>,
): SiteAnalyticsTableRow[] {
    const comparisonByKey = new Map<string, StatisticsPage>();

    if (comparisonBounceRatePages) {
        for (const page of comparisonBounceRatePages) {
            const pageKey = getComparisonKey(page);
            if (pageKey && !comparisonByKey.has(pageKey)) {
                comparisonByKey.set(pageKey, page);
            }
        }
    }

    const totalVisits = currentBounceRatePages.reduce((sum, page) => sum + toInteger(page.visits), 0);

    return currentBounceRatePages.map((page, index) => {
        const pageKey = getComparisonKey(page);
        const comparisonPage = pageKey ? comparisonByKey.get(pageKey) : undefined;
        const url = String(page.url || '');
        const title = String(page.title || '');

        const visits = toInteger(page.visits);
        const entries = toInteger(page.entry_visits);
        const entriesShare = totalVisits > 0 ? (entries / totalVisits) * 100 : 0;
        const bounceRateValue = toBounceRate(page);

        const comparisonVisits = comparisonPage ? toInteger(comparisonPage.visits) : null;
        const comparisonEntries = comparisonPage ? toInteger(comparisonPage.entry_visits) : null;
        const comparisonBounceRateValue = comparisonPage ? toBounceRate(comparisonPage) : null;

        return {
            id: `${url || pageKey || 'row'}-${index}`,
            page: {
                label: url,
                href: url,
                ...(title && title !== url ? { tooltip: title } : {}),
            },
            visits,
            visitsComparison: comparisonVisits,
            bounceRate: formatPercentage(bounceRateValue),
            bounceRateComparison: comparisonBounceRateValue === null
                ? null
                : formatPercentage(comparisonBounceRateValue),
            entries: formatCountWithPercent(entries, entriesShare),
            entriesComparison: comparisonEntries,
            avgTime: toInteger(page.avg_time),
            avgTimeComparison: comparisonPage ? toInteger(comparisonPage.avg_time) : null,
            bounceRateValue,
            bounceRateComparisonValue: comparisonBounceRateValue,
        };
    });
}

function getPageLabel (row: SiteAnalyticsTableRow): string {
    const linkCell = toLinkCell(row.page);
    if (linkCell) {
        return getUrlPath(linkCell.label);
    }

    if (typeof row.page === 'string') {
        return getUrlPath(row.page);
    }

    return '';
}

function getUrlPath (value: string): string {
    try {
        const url = new URL(value);

        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return value;
    }
}

function getComparisonKey (page: StatisticsPage): string {
    return String(page.url || page.label || page.title || '').trim();
}

function toBounceRate (page: StatisticsPage): number {
    const bounceRateFromField = toFloat(page.bounce_rate);
    if (bounceRateFromField > 0) {
        return bounceRateFromField;
    }

    const bounces = toFloat(page.bounces);
    const visitors = toFloat(page.visitors);

    if (visitors > 0) {
        return (bounces / visitors) * 100;
    }

    return 0;
}

function formatCountWithPercent (count: number, percent: number): string {
    return `${formatInteger(count)} (${formatPercentage(percent)})`;
}

function formatInteger (value: number): string {
    return value.toLocaleString();
}

function formatPercentage (value: number): string {
    const safeValue = Number.isFinite(value) ? value : 0;
    return `${safeValue.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function toLinkCell (value: unknown): SiteAnalyticsLinkCell | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const link = value as { label?: unknown; href?: unknown; tooltip?: unknown };
    if (typeof link.label !== 'string' || typeof link.href !== 'string') {
        return null;
    }

    return {
        label: link.label,
        href: link.href,
        ...(typeof link.tooltip === 'string' && link.tooltip ? { tooltip: link.tooltip } : {}),
    };
}

function toInteger (value: unknown): number {
    const parsed = Number.parseInt(String(value ?? 0), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function toFloat (value: unknown): number {
    const parsed = Number.parseFloat(String(value ?? 0));
    return Number.isNaN(parsed) ? 0 : parsed;
}

function getTotal (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'total' in collection) {
        const total = Number((collection as { total?: unknown }).total);
        if (!Number.isNaN(total) && total > 0) {
            return total;
        }
    }

    return fallback;
}

function getPerPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'perPage' in collection) {
        const perPage = Number((collection as { perPage?: unknown }).perPage);
        if (!Number.isNaN(perPage) && perPage > 0) {
            return perPage;
        }
    }

    return fallback;
}

function getCurrentPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'currentPage' in collection) {
        const currentPage = Number((collection as { currentPage?: unknown }).currentPage);
        if (!Number.isNaN(currentPage) && currentPage > 0) {
            return currentPage;
        }
    }

    return fallback;
}

export const BOUNCE_RATE_PAGES_DEFINITION: SiteAnalyticsPageDefinition = {
    key: 'bounceRatePages',
    config: BOUNCE_RATE_PAGES_CONFIG,
    loadData: loadBounceRatePagesData,
};

export const BOUNCE_RATE_PAGES_PAGE_CONFIG = BOUNCE_RATE_PAGES_DEFINITION.config;
